import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Modal, ModalHeader, ModalContent, ModalFooterButtons, Text } from '@vibe/core';
import TopBar from './TopBar';
import SyncfusionScheduler, { type SyncfusionSchedulerHandle, type WorklogEvent } from './SyncfusionScheduler';
import CreateItemDialog from './CreateItemDialog';
import CellContextMenu from './CellContextMenu';
import LeaveRequestDialog from '../leave/LeaveRequestDialog';
import { getLeaveRequests, getLeaveTypes, recallLeaveRequest, approveLeaveRequest, rejectLeaveRequest, revokeLeaveRequest, updateLeaveRequest, type LeaveRequest } from '../../api/leave.api';
import TaskEditDialog from './TaskEditDialog';
import TaskInfoTooltip from './TaskInfoTooltip';
import LeaveInfoTooltip from './LeaveInfoTooltip';
import LogWorkDialog from './LogWorkDialog';
import ItemWorklogsDialog from './ItemWorklogsDialog';
import ItemSearchPanel from './ItemSearchPanel';
import WorklogDetailPopup from './WorklogDetailPopup';
import MultiSelectToolbar from './MultiSelectToolbar';
import BoardSettings from '../settings/BoardSettings';
import FilterBar from '../filter/FilterBar';
import { useScheduleStore, type ScheduleItem, calculateFetchRange, isRangeContained } from '../../stores/scheduleStore';
import { useFilterStore } from '../../stores/filterStore';
import { type LeaveEntry } from '../../stores/leaveStore';
import { useUsersStore } from '../../stores/usersStore';
import { useWorkloadStore } from '../../stores/workloadStore';
import { useMondayContext } from '../../context/MondayContext';
import { useCurrentUserAccess } from '../../hooks/useCurrentUserAccess';
import { useSchedule } from '../../hooks/useSchedule';
import { useWorkload } from '../../hooks/useWorkload';
import { useHolidays } from '../../hooks/useHolidays';
import { getBoardConfig, getFieldMappingLocal, getSubitemFieldMappingLocal, getPerPersonDistributionLocal, getItemLevelLocal, isBoardConfigured } from '../../api/board-config.api';
import { getMyApprovees } from '../../api/settings.api';
import { getMyViewableTargets } from '../../api/permission.api';
import { getAppSettings } from '../../api/settings.api';
import { fetchMondayUsers, fetchFirstDayOfWeek, updateMondayItem, fetchBoardGroups, fetchSubitemsBoardInfo } from '../../api/monday.api';
import { updateItemTimeline, updateItemAssignee } from '../../api/schedule.api';
import { buildReassignedEntries, redistributeHours } from '../../utils/assignee-helpers';
import { resolveApprovees } from '../../utils/approvee-resolution';
import { deleteWorklog } from '../../api/worklog.api';
import FieldMappingWizard from '../settings/FieldMappingWizard';
import { format } from 'date-fns';
import { exportSchedulerExcel } from '../../utils/export-excel';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

// ─── Props ────────────────────────────────────────────────────────────────────

export interface SchedulerBoardProps {
  /** Optional when running in leaves-only mode (no boards selected) */
  boardId?: number;
  /** When used in Dashboard Widget, multiple boards may be aggregated */
  multiBoardIds?: number[];
  /** Board names for multi-board workspace filtering (id → name) */
  multiBoardOptions?: Array<{ id: number; name: string }>;
}

// ─── Component ────────────────────────────────────────────────────────────────

const SchedulerBoard: React.FC<SchedulerBoardProps> = ({
  boardId: boardIdProp,
  multiBoardIds,
  multiBoardOptions,
}) => {
  // Leaves-only mode: no board context → show leaves/holidays only, hide item UI
  const noBoardsMode = !boardIdProp && (!multiBoardIds || multiBoardIds.length === 0);
  // Internal board id is always a number; `0` is the placeholder used in noBoardsMode.
  // All effects that would call board-scoped APIs with `0` must gate on `noBoardsMode`.
  const boardId = boardIdProp ?? 0;
  const { context, isAdmin } = useMondayContext();
  const { isAdmin: isAppAdmin } = useCurrentUserAccess();
  const { viewMode, dateRange, fetchedRange, items, isLoading, error } = useScheduleStore();
  const { assignees: filterAssignees, teams: filterTeams, selectedBoardIds: filterBoardIds, completionFilter, groupByMode, setGroupByMode, showLeaves, showHolidays, setViewScope } = useFilterStore();
  const { users } = useUsersStore();
  const { enabled: workloadEnabled, perPersonDistribution, indicatorType } = useWorkloadStore();
  const { fetchSchedule, fetchMultiBoardSchedule, bulkUnplanItems, bulkReassignItems, bulkMoveItems } = useSchedule();
  const { computeWorkload, fetchDefaultSchedule, invalidateWorklogCache } = useWorkload();
  const { holidays, userHolidays, allUserHolidays, fetchHolidays } = useHolidays();
  const schedulerRef = useRef<SyncfusionSchedulerHandle>(null);

  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isSearchPanelOpen, setIsSearchPanelOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [showWizard, setShowWizard] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [createDialogState, setCreateDialogState] = useState<{
    open: boolean;
    date?: Date;
    endDate?: Date;
    assigneeId?: number;
    assigneeKind?: 'person' | 'team';
  }>({ open: false });
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveDialogDate, setLeaveDialogDate] = useState<string | undefined>();
  const [leaveDialogEndDate, setLeaveDialogEndDate] = useState<string | undefined>();
  const [leaveDialogForUserId, setLeaveDialogForUserId] = useState<number | undefined>();
  // Cell context menu state — opened on single-click of an empty scheduler cell.
  // Replaces the previous double-click → CreateItemDialog flow.
  const [cellMenu, setCellMenu] = useState<{
    x: number;
    y: number;
    date: Date;
    endDate?: Date;
    assigneeId: number;
    assigneeKind: 'person' | 'team';
  } | null>(null);
  // LogWorkDialog in time-tracking mode (no preselected task) — opened from the
  // cell context menu's "Log time" option.
  const [logTimeDialog, setLogTimeDialog] = useState<{
    initialDate: string;
    initialEndDate?: string;
    forUserId?: string;
    forUserName?: string;
  } | null>(null);
  // Shown when the user picks "Create item" from the cell menu in no-boards mode.
  const [showNoBoardsPrompt, setShowNoBoardsPrompt] = useState(false);

  // Users the current user can act on behalf of. Split by action type so a
  // leave approver can't log time for their approvees (and vice versa):
  //  - `leaveApproveeIds`: users whose default leave approver is the current
  //    user (settings.api `getMyApprovees`). Controls `canRequestLeave` only.
  //  - `timesheetApproveeIds` (derived below): users the current user can
  //    approve timesheets for. Controls `canLogTime` only. Resolved from the
  //    raw `timesheet_approve` rules returned by `getMyViewableTargets`:
  //      * targetType 'user'  → the target user id
  //      * targetType 'team'  → every user who belongs to that team (resolved
  //        against `users` from the Monday users store)
  //      * targetType 'all'   → sets `canLogTimeForAnyUser = true`
  //  - Admins/app-admins bypass both gates.
  const [leaveApproveeIds, setLeaveApproveeIds] = useState<Set<number>>(
    () => new Set(),
  );
  const [timesheetApproveTargets, setTimesheetApproveTargets] = useState<
    Array<{ targetType: string; targetId: string | null }>
  >([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [leaveApprovees, viewable] = await Promise.all([
          getMyApprovees().catch(() => [] as Array<{ mondayUserId: string }>),
          getMyViewableTargets().catch(() => ({ canViewAll: false, targets: [] as Array<{ targetType: string; targetId: string | null; permission: string }> })),
        ]);
        if (cancelled) return;

        const leaveIds = new Set<number>();
        for (const a of leaveApprovees) {
          const n = Number(a.mondayUserId);
          if (n) leaveIds.add(n);
        }

        const approveTargets = viewable.targets
          .filter((t) => t.permission === 'timesheet_approve')
          .map((t) => ({ targetType: t.targetType, targetId: t.targetId }));

        setLeaveApproveeIds(leaveIds);
        setTimesheetApproveTargets(approveTargets);
      } catch {
        // Non-fatal: the cell menu just falls back to self/admin-only gating.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve team-target approve rules against the Monday users list (which
  // already carries each user's team memberships). Recomputed whenever either
  // the rule set or the users list changes.
  const { timesheetApproveeIds, canLogTimeForAnyUser } = useMemo(
    () => resolveApprovees(timesheetApproveTargets, users),
    [timesheetApproveTargets, users],
  );
  const [boardGroups, setBoardGroups] = useState<Array<{ id: string; name: string }>>([]);

  // Tooltip state: shown when user clicks an event (before opening edit/log work)
  const [tooltipState, setTooltipState] = useState<{
    taskId: string;
    position: { x: number; y: number };
  } | null>(null);

  // Leave tooltip state: shown when user clicks a leave event
  const [leaveTooltipState, setLeaveTooltipState] = useState<{
    leaveId: string;
    position: { x: number; y: number };
  } | null>(null);

  // Log Work dialog state: separate from edit dialog
  const [logWorkTaskId, setLogWorkTaskId] = useState<string | null>(null);


  // Leave data from backend API (replaces local leaveStore)
  const [leaves, setLeaves] = useState<LeaveEntry[]>([]);
  // Leaves covering the full item timeline range (for workload computation).
  // When items extend beyond the buffered view range, this includes additional
  // leaves so computeHoursPerDay distributes hours correctly.
  const [workloadLeaves, setWorkloadLeaves] = useState<LeaveEntry[]>([]);
  // Raw backend leave requests (kept for passing to edit dialog)
  const [rawLeaveRequests, setRawLeaveRequests] = useState<LeaveRequest[]>([]);
  // Leave being edited (passed to LeaveRequestDialog in edit mode)
  const [editingLeaveRequest, setEditingLeaveRequest] = useState<LeaveRequest | null>(null);

  // Manual refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ── Set filter persistence scope ─────────────────────────────────────────
  const viewScope = multiBoardIds && multiBoardIds.length > 1 ? 'workspace' as const : 'board' as const;
  useEffect(() => {
    setViewScope(viewScope);
  }, [viewScope, setViewScope]);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * Re-fetch schedule items using the current fetched (buffered) range.
   * Used after mutations (create, duplicate, external drop, etc.) to refresh
   * without narrowing the pre-fetched buffer.
   */
  const refetchSchedule = useCallback((options?: { silent?: boolean }) => {
    if (noBoardsMode) return;
    const { fetchedRange: currentFetchedRange, dateRange: currentDateRange, viewMode: currentViewMode } = useScheduleStore.getState();
    const range = currentFetchedRange ?? calculateFetchRange(currentDateRange, currentViewMode);
    if (multiBoardIds && multiBoardIds.length > 1) {
      fetchMultiBoardSchedule(multiBoardIds, range.start, range.end, options);
    } else {
      fetchSchedule(boardId, range.start, range.end, options);
    }
  }, [noBoardsMode, boardId, multiBoardIds, fetchSchedule, fetchMultiBoardSchedule]);

  // Fetch monday.com users and account first day of week on mount
  useEffect(() => {
    const { setUsers, setLoading, setError } = useUsersStore.getState();
    setLoading(true);
    fetchMondayUsers()
      .then((fetchedUsers) => {
        setUsers(fetchedUsers);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load users');
      })
      .finally(() => {
        setLoading(false);
      });

    // Fetch default capacity scheme for non-working day coloring
    fetchDefaultSchedule();

    // Fetch account's first day of week setting (cached + deduplicated)
    fetchFirstDayOfWeek()
      .then((day) => {
        useScheduleStore.getState().setFirstDayOfWeek(day);
      })
      .catch(() => {
        // Default to Monday if query fails
      });
  }, []);

  // Fetch board config - check if field mapping is configured + load workload settings
  // Resolves null board-level values from account-level defaults
  useEffect(() => {
    // Reset states when boardId changes to avoid showing stale wizard/data
    setConfigLoaded(false);
    setShowWizard(false);
    // Invalidate fetched range so the new board triggers a fresh fetch
    useScheduleStore.getState().setFetchedRange(null);

    // Leaves-only mode: no board to configure. Mark as loaded so leaves/holidays
    // effects can proceed, and clear any stale items from a previous board.
    // IMPORTANT: do NOT call getBoardConfig here — it has a side effect of creating
    // a default config row in the DB (see CLAUDE.md: "Board Config API — Side Effects").
    if (noBoardsMode) {
      useScheduleStore.getState().setItems([]);
      setConfigLoaded(true);
      return;
    }

    const load = async () => {
      try {
        const config = await getBoardConfig(boardId);
        if (!isBoardConfigured(config)) {
          setShowWizard(true);
        }

        // Resolve per-person distribution: board override > account default > hardcoded fallback
        let perPersonDistribution = config.perPersonDistribution;

        if (perPersonDistribution === null) {
          try {
            const appSettings = await getAppSettings();
            perPersonDistribution = appSettings.perPersonDistribution;
          } catch {
            // Fallback to safe defaults if account settings unavailable
          }
        }

        const { setPerPersonDistribution } = useWorkloadStore.getState();
        setPerPersonDistribution(perPersonDistribution ?? false);
        setConfigLoaded(true);
      } catch {
        setShowWizard(true);
        setConfigLoaded(true);
      }
    };
    load();
  }, [boardId, noBoardsMode]);

  // Fetch board groups for Create Item dialog
  useEffect(() => {
    if (!configLoaded || showWizard || noBoardsMode) return;
    fetchBoardGroups(boardId).then((groups) => {
      setBoardGroups(groups.map((g) => ({ id: g.id, name: g.title })));
    }).catch(() => {
      // silently fail - groups are optional
    });
  }, [boardId, configLoaded, showWizard, noBoardsMode]);

  // Fetch schedule data when board or date range changes.
  // Uses a buffered fetch range so navigating 1 step forward/backward doesn't refetch.
  useEffect(() => {
    if (!configLoaded || showWizard || noBoardsMode) return;

    // Skip fetch if the visible range is already covered by the previously fetched range
    if (isRangeContained(dateRange, fetchedRange)) return;

    const bufferedRange = calculateFetchRange(dateRange, viewMode);
    const { setFetchedRange } = useScheduleStore.getState();

    // Set fetchedRange immediately (synchronously) to prevent concurrent useEffect
    // triggers from starting duplicate fetches before the async call completes.
    setFetchedRange(bufferedRange);

    const fetchPromise = multiBoardIds && multiBoardIds.length > 1
      ? fetchMultiBoardSchedule(multiBoardIds, bufferedRange.start, bufferedRange.end)
      : fetchSchedule(boardId, bufferedRange.start, bufferedRange.end);

    fetchPromise.catch(() => {
      // On failure, reset so the next trigger retries
      setFetchedRange(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId, multiBoardIds, dateRange.start, dateRange.end, configLoaded, showWizard, noBoardsMode]);

  // Collect all known user IDs as strings for holiday assignment (default calendars apply to all)
  const allKnownUserIds = useMemo(() => {
    return users
      .filter((u) => u.id !== 0)
      .map((u) => String(u.id));
  }, [users]);

  // Fetch holidays for the buffered date range
  useEffect(() => {
    if (!configLoaded || showWizard) return;
    if (isRangeContained(dateRange, fetchedRange)) return;

    const bufferedRange = calculateFetchRange(dateRange, viewMode);
    fetchHolidays(bufferedRange.start, bufferedRange.end, allKnownUserIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.start, dateRange.end, configLoaded, showWizard, allKnownUserIds]);

  // Fetch leave requests from backend for the buffered date range.
  // Accepts explicit date range so it can be called with buffered dates or on-demand with current range.
  const fetchLeaves = useCallback(async (startDate?: string, endDate?: string) => {
    const fetchStart = startDate ?? calculateFetchRange(dateRange, viewMode).start;
    const fetchEnd = endDate ?? calculateFetchRange(dateRange, viewMode).end;
    try {
      // Fetch first page + leave types in parallel
      const [firstPage, leaveTypes] = await Promise.all([
        getLeaveRequests({
          dateFrom: fetchStart,
          dateTo: fetchEnd,
          pageSize: 100,
          page: 1,
        }),
        getLeaveTypes(),
      ]);

      // Fetch remaining pages if there are more results
      let requests = firstPage.data;
      const totalPages = Math.ceil(firstPage.total / 100);
      if (totalPages > 1) {
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            getLeaveRequests({
              dateFrom: fetchStart,
              dateTo: fetchEnd,
              pageSize: 100,
              page: i + 2,
            }),
          ),
        );
        requests = requests.concat(remaining.flatMap((r) => r.data));
      }

      // Build a leave-type-id → name lookup map
      const typeNameMap = new Map<string, string>();
      for (const lt of leaveTypes) {
        typeNameMap.set(lt.id, lt.name);
      }

      // Map backend LeaveRequest → LeaveEntry for the scheduler
      const mapped: LeaveEntry[] = requests.map((r) => ({
        id: r.id,
        assigneeId: Number(r.monday_user_id),
        leaveType: typeNameMap.get(r.leave_type_id) ?? 'Leave',
        startDate: typeof r.date_from === 'string'
          ? r.date_from.split('T')[0]
          : (() => { const d = new Date(r.date_from); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
        endDate: typeof r.date_to === 'string'
          ? r.date_to.split('T')[0]
          : (() => { const d = new Date(r.date_to); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
        dayType: r.capacity_type === 'half_day' ? 'am' as const : 'full' as const,
        note: '',
        status: (r.status.charAt(0).toUpperCase() + r.status.slice(1)) as LeaveEntry['status'],
        approverId: Number(r.approver_user_id),
        createdByUserId: r.created_by_user_id ?? null,
        createdAt: r.created_at,
      }));
      setLeaves(mapped);
      setRawLeaveRequests(requests);
    } catch {
      // Silently fail – leaves are optional for the scheduler
    }
  }, [dateRange, viewMode]);

  useEffect(() => {
    if (!configLoaded || showWizard) return;
    if (isRangeContained(dateRange, fetchedRange)) return;

    const bufferedRange = calculateFetchRange(dateRange, viewMode);
    fetchLeaves(bufferedRange.start, bufferedRange.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configLoaded, showWizard, dateRange.start, dateRange.end]);

  // Compute the min/max item timeline dates as stable strings so the extended
  // leave fetch below only re-triggers when boundaries actually change, not on
  // every item drag/edit.
  const itemMinStart = useMemo(() => {
    if (items.length === 0) return '';
    let min = items[0].startDate;
    for (const item of items) {
      if (item.startDate < min) min = item.startDate;
    }
    return min;
  }, [items]);

  const itemMaxEnd = useMemo(() => {
    if (items.length === 0) return '';
    let max = items[0].endDate;
    for (const item of items) {
      if (item.endDate > max) max = item.endDate;
    }
    return max;
  }, [items]);

  // Fetch leaves for the full item timeline range so workload computation
  // correctly distributes hours for items that extend beyond the buffered view.
  useEffect(() => {
    if (!workloadEnabled || !itemMinStart || leaves.length === 0) {
      // If no items or no leaves in view, the view leaves are sufficient
      setWorkloadLeaves(leaves);
      return;
    }

    // Check if items extend beyond the buffered fetch range
    const bufferedRange = calculateFetchRange(dateRange, viewMode);
    const needsEarlier = itemMinStart < bufferedRange.start;
    const needsLater = itemMaxEnd > bufferedRange.end;

    if (!needsEarlier && !needsLater) {
      // Items fit within the buffered range — view leaves are sufficient
      setWorkloadLeaves(leaves);
      return;
    }

    // Fetch leaves for the extended range
    const extStart = needsEarlier ? itemMinStart : bufferedRange.start;
    const extEnd = needsLater ? itemMaxEnd : bufferedRange.end;

    let cancelled = false;
    (async () => {
      try {
        const [firstPage, leaveTypes] = await Promise.all([
          getLeaveRequests({ dateFrom: extStart, dateTo: extEnd, pageSize: 100, page: 1 }),
          getLeaveTypes(),
        ]);
        if (cancelled) return;

        let requests = firstPage.data;
        const totalPages = Math.ceil(firstPage.total / 100);
        if (totalPages > 1) {
          const remaining = await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, i) =>
              getLeaveRequests({ dateFrom: extStart, dateTo: extEnd, pageSize: 100, page: i + 2 }),
            ),
          );
          if (cancelled) return;
          requests = requests.concat(remaining.flatMap((r) => r.data));
        }

        const typeNameMap = new Map<string, string>();
        for (const lt of leaveTypes) {
          typeNameMap.set(lt.id, lt.name);
        }

        const mapped: LeaveEntry[] = requests.map((r) => ({
          id: r.id,
          assigneeId: Number(r.monday_user_id),
          leaveType: typeNameMap.get(r.leave_type_id) ?? 'Leave',
          startDate: typeof r.date_from === 'string'
            ? r.date_from.split('T')[0]
            : (() => { const d = new Date(r.date_from); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
          endDate: typeof r.date_to === 'string'
            ? r.date_to.split('T')[0]
            : (() => { const d = new Date(r.date_to); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })(),
          dayType: r.capacity_type === 'half_day' ? 'am' as const : 'full' as const,
          note: '',
          status: (r.status.charAt(0).toUpperCase() + r.status.slice(1)) as LeaveEntry['status'],
          approverId: Number(r.approver_user_id),
          createdByUserId: r.created_by_user_id ?? null,
          createdAt: r.created_at,
        }));

        if (!cancelled) setWorkloadLeaves(mapped);
      } catch {
        // Fall back to view-range leaves on error
        if (!cancelled) setWorkloadLeaves(leaves);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workloadEnabled, itemMinStart, itemMaxEnd, leaves, dateRange.start, dateRange.end, viewMode]);

  // ── Manual refresh ──────────────────────────────────────────────────────────

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);

    const range = calculateFetchRange(dateRange, viewMode);

    // Clear the fetched range so the schedule fetch isn't skipped by the buffer check
    useScheduleStore.getState().setFetchedRange({ start: '', end: '' });

    try {
      const schedulePromise = noBoardsMode
        ? Promise.resolve()
        : multiBoardIds && multiBoardIds.length > 1
          ? fetchMultiBoardSchedule(multiBoardIds, range.start, range.end).then(() => {
              useScheduleStore.getState().setFetchedRange(range);
            })
          : fetchSchedule(boardId, range.start, range.end).then(() => {
              useScheduleStore.getState().setFetchedRange(range);
            });
      await Promise.all([
        schedulePromise,
        fetchHolidays(range.start, range.end, allKnownUserIds),
        fetchLeaves(range.start, range.end),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, dateRange, viewMode, boardId, multiBoardIds, noBoardsMode, fetchSchedule, fetchMultiBoardSchedule, fetchHolidays, fetchLeaves, allKnownUserIds]);

  // ── Derived data ───────────────────────────────────────────────────────────

  const userMap = useMemo(() => {
    const map = new Map<number, { id: number; name: string; avatarUrl?: string }>();
    for (const user of users) {
      map.set(user.id, {
        id: user.id,
        name: user.name,
        avatarUrl: user.photo_thumb_small ?? undefined,
      });
    }
    return map;
  }, [users]);

  const assigneeOptions = useMemo(() => {
    const seen = new Map<number, { id: number; name: string; avatarUrl?: string }>();
    // Include all users (not just those with items) so the Create dialog can assign to anyone
    for (const user of users) {
      seen.set(user.id, { id: user.id, name: user.name, avatarUrl: user.photo_thumb_small ?? undefined });
    }
    return Array.from(seen.values());
  }, [users]);

  // Build team options from monday.com user data (with member names for the dropdown description)
  const teamOptions = useMemo(() => {
    const teamMap = new Map<number, { name: string; memberNames: string[] }>();
    for (const user of users) {
      for (const team of user.teams) {
        let entry = teamMap.get(team.id);
        if (!entry) {
          entry = { name: team.name, memberNames: [] };
          teamMap.set(team.id, entry);
        }
        if (!entry.memberNames.includes(user.name)) {
          entry.memberNames.push(user.name);
        }
      }
    }
    return Array.from(teamMap, ([id, { name, memberNames }]) => ({ id, name, memberNames }));
  }, [users]);

  // Build team → member mapping for the scheduler's team resource grouping
  const teamMemberMap = useMemo(() => {
    const map = new Map<number, { team: { id: number; name: string }; memberIds: number[] }>();
    for (const user of users) {
      for (const team of user.teams) {
        let entry = map.get(team.id);
        if (!entry) {
          entry = { team: { id: team.id, name: team.name }, memberIds: [] };
          map.set(team.id, entry);
        }
        if (!entry.memberIds.includes(user.id)) {
          entry.memberIds.push(user.id);
        }
      }
    }
    return map;
  }, [users]);

  /** Resolve display name for an item's assignee (user name or team name) */
  const getAssigneeName = useCallback(
    (item: { assigneeId: number; assignedTeamId?: number }) => {
      if (item.assigneeId > 0) {
        return userMap.get(item.assigneeId)?.name ?? `User ${item.assigneeId}`;
      }
      if (item.assignedTeamId && teamMemberMap) {
        return teamMemberMap.get(item.assignedTeamId)?.team.name ?? `Team ${item.assignedTeamId}`;
      }
      return 'Unassigned';
    },
    [userMap, teamMemberMap],
  );

  const filteredItems = useMemo(() => {
    let result = items;

    // Board filter (workspace multi-board mode)
    if (filterBoardIds.length > 0) {
      const boardSet = new Set(filterBoardIds);
      result = result.filter((item) => item.boardId !== undefined && boardSet.has(item.boardId));
    }

    // Completion status filter
    if (completionFilter === 'incomplete') {
      result = result.filter((item) => !item.isDone);
    } else if (completionFilter === 'completed') {
      result = result.filter((item) => item.isDone === true);
    }

    // In users mode, selection is required — no selection means no items
    // Also expand selected teams into their member user IDs
    if (groupByMode === 'assignees') {
      if (filterAssignees.length === 0 && filterTeams.length === 0) return [];
      const allowedUsers = new Set(filterAssignees);
      for (const teamId of filterTeams) {
        const entry = teamMemberMap.get(teamId);
        if (entry) {
          for (const memberId of entry.memberIds) {
            allowedUsers.add(memberId);
          }
        }
      }
      result = result.filter((item) => allowedUsers.has(item.assigneeId));
    }

    // In teams-only mode, show ONLY items directly assigned to the selected teams
    if (groupByMode === 'teams' && filterTeams.length > 0) {
      const selectedTeamSet = new Set(filterTeams);
      result = result.filter((item) =>
        item.assignedTeamId !== undefined && selectedTeamSet.has(item.assignedTeamId),
      );
    }

    // In teamWithUsers mode, show items assigned to teams AND items assigned to team members
    if (groupByMode === 'teamWithUsers' && filterTeams.length > 0) {
      const allowedUsers = new Set<number>();
      const selectedTeamSet = new Set(filterTeams);
      for (const teamId of filterTeams) {
        const entry = teamMemberMap.get(teamId);
        if (entry) {
          for (const memberId of entry.memberIds) {
            allowedUsers.add(memberId);
          }
        }
      }
      result = result.filter((item) =>
        allowedUsers.has(item.assigneeId) ||
        (item.assignedTeamId !== undefined && selectedTeamSet.has(item.assignedTeamId)),
      );
    }

    return result;
  }, [items, filterAssignees, filterTeams, filterBoardIds, completionFilter, groupByMode, teamMemberMap]);

  // In assignees mode, restrict resource rows to only selected users + team members
  const filteredUserMap = useMemo(() => {
    if (groupByMode !== 'assignees') return userMap;
    if (filterAssignees.length === 0 && filterTeams.length === 0) return userMap;
    const allowedIds = new Set(filterAssignees);
    for (const teamId of filterTeams) {
      const entry = teamMemberMap.get(teamId);
      if (entry) {
        for (const memberId of entry.memberIds) {
          allowedIds.add(memberId);
        }
      }
    }
    const filtered = new Map<number, { id: number; name: string; avatarUrl?: string }>();
    for (const id of allowedIds) {
      const user = userMap.get(id);
      if (user) filtered.set(id, user);
    }
    return filtered;
  }, [groupByMode, userMap, filterAssignees, filterTeams, teamMemberMap]);

  // ── Excel export ────────────────────────────────────────────────────────────

  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const visibleUsers = Array.from(filteredUserMap.values());
      const { entriesByUser } = useWorkloadStore.getState();
      const wlState = useWorkloadStore.getState();
      await exportSchedulerExcel({
        dateRange,
        items: filteredItems,
        leaves,
        userHolidays,
        users: visibleUsers,
        hideWeekends: useScheduleStore.getState().hideWeekends,
        workloadByUser: entriesByUser,
        indicatorType: wlState.indicatorType,
        worklogsByItem: wlState.worklogsByItem,
      });
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, filteredUserMap, dateRange, filteredItems, leaves, userHolidays]);

  // Recompute workload when enabled and filtered items/date range/settings change
  // Pass filteredItems so workload reflects board/user/team filter selections
  // Use workloadLeaves (extended range) and allUserHolidays (unfiltered) so that
  // items spanning beyond the visible range get correct hour distribution.
  useEffect(() => {
    if (!workloadEnabled) return;
    const visibleUserIds = users.filter((u) => u.id !== 0).map((u) => u.id);
    const approvedLeaves = workloadLeaves.filter((l) => l.status === 'Approved');
    computeWorkload(visibleUserIds, filteredItems, approvedLeaves, allUserHolidays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workloadEnabled, filteredItems, users, dateRange.start, dateRange.end, perPersonDistribution, workloadLeaves, allUserHolidays, indicatorType]);

  // ── Worklog events for worklog_workload mode ─────────────────────────────

  // Derive worklog events from the store's rawWorklogs (fetched by useWorkload)
  const storeRawWorklogs = useWorkloadStore((s) => s.rawWorklogs);

  const worklogEventsData = useMemo<WorklogEvent[]>(() => {
    if (indicatorType !== 'worklog_workload' || storeRawWorklogs.length === 0) return [];

    const itemNameMap = new Map<string, string>();
    for (const item of items) {
      itemNameMap.set(String(item.mondayItemId), item.name);
    }

    return storeRawWorklogs.map((wl) => ({
      id: wl.id,
      mondayItemId: wl.mondayItemId,
      mondayBoardId: wl.mondayBoardId,
      mondayUserId: Number(wl.mondayUserId),
      date: wl.date,
      hours: wl.hours,
      description: wl.description,
      itemName: itemNameMap.get(wl.mondayItemId) || undefined,
    }));
  }, [indicatorType, storeRawWorklogs, items]);

  // ── Worklog click popup state ───────────────────────────────────────────────

  const [worklogPopup, setWorklogPopup] = useState<{
    worklogId: string;
    position: { x: number; y: number };
  } | null>(null);

  const [itemWorklogsDialog, setItemWorklogsDialog] = useState<{
    mondayItemId: number;
    itemName: string;
    position: { x: number; y: number };
  } | null>(null);

  const handleWorklogClick = useCallback((worklogId: string, position?: { x: number; y: number }) => {
    if (position) {
      setWorklogPopup({ worklogId, position });
    }
  }, []);

  const handleWorklogDelete = useCallback(async (worklogId: string) => {
    try {
      await deleteWorklog(worklogId);
      setWorklogPopup(null);
      // Worklogs changed — invalidate cache and recompute
      invalidateWorklogCache();
      const visibleUserIds = users.filter((u) => u.id !== 0).map((u) => u.id);
      const approvedLeaves = workloadLeaves.filter((l) => l.status === 'Approved');
      computeWorkload(visibleUserIds, filteredItems, approvedLeaves, allUserHolidays);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete worklog';
      monday.execute('notice', {
        message: msg,
        type: 'error',
      } as never);
    }
  }, [users, workloadLeaves, filteredItems, allUserHolidays, computeWorkload, invalidateWorklogCache]);

  // ── Multi-select handlers ──────────────────────────────────────────────────

  const handleUnplan = useCallback(
    async (itemIds: string[]) => {
      await bulkUnplanItems(itemIds, boardId);
      setSelectedItemIds([]);
    },
    [bulkUnplanItems, boardId],
  );

  const handleReassign = useCallback(
    async (itemIds: string[], targetUserId: number) => {
      await bulkReassignItems(itemIds, targetUserId, boardId);
      setSelectedItemIds([]);
    },
    [bulkReassignItems, boardId],
  );

  const handleMoveToDate = useCallback(
    async (itemIds: string[], targetDate: string) => {
      const firstItem = items.find((i) => i.id === itemIds[0]);
      if (!firstItem) return;
      const start = new Date(firstItem.startDate);
      const target = new Date(targetDate);
      const daysDelta = Math.round((target.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      if (daysDelta !== 0) {
        await bulkMoveItems({ itemIds, daysDelta }, boardId);
      }
      setSelectedItemIds([]);
    },
    [items, bulkMoveItems, boardId],
  );

  const handleShiftDuration = useCallback(
    async (itemIds: string[], daysDelta: number) => {
      await bulkMoveItems({ itemIds, daysDelta }, boardId);
      setSelectedItemIds([]);
    },
    [bulkMoveItems, boardId],
  );

  // ── External drag handler (from ItemSearchPanel) ───────────────────────────

  const handleExternalDrop = useCallback(
    async (mondayItemId: number, date: Date, assigneeId: number, assigneeKind: 'person' | 'team' = 'person') => {
      const dateStr = format(date, 'yyyy-MM-dd');

      // Look up item details from the search panel store for better optimistic display.
      // Subitems are nested inside parent items, so search both levels.
      const { useItemSearchStore } = await import('../../stores/itemSearchStore');
      let searchItem: import('../../stores/itemSearchStore').BoardItem | undefined;
      for (const item of useItemSearchStore.getState().items) {
        if (item.mondayItemId === mondayItemId) {
          searchItem = item;
          break;
        }
        if (item.subitems) {
          const sub = item.subitems.find((s) => s.mondayItemId === mondayItemId);
          if (sub) {
            searchItem = sub;
            break;
          }
        }
      }

      // Resolve the correct board for the dropped item (multi-board workspace support)
      const effectiveBoardId = searchItem?.boardId ?? boardId;
      const isSubitem = searchItem?.isSubitem ?? false;
      const subitemsBoardId = searchItem?.subitemsBoardId;

      // Block items that don't match the board's item level configuration
      const dropItemLevel = getItemLevelLocal(effectiveBoardId);
      if (!isSubitem && dropItemLevel === 'sub') {
        monday.execute('notice', {
          message: 'This board is configured for subitems only. Please expand the parent item in the Plan Item panel and drag a subitem instead.',
          type: 'error',
        } as never);
        return;
      }
      if (isSubitem && dropItemLevel === 'main') {
        monday.execute('notice', {
          message: 'This board is configured for main items only. Subitems cannot be scheduled on this board.',
          type: 'error',
        } as never);
        return;
      }

      // Use subitem field mapping when dropping a subitem
      const fieldMapping = isSubitem
        ? getSubitemFieldMappingLocal(effectiveBoardId)
        : getFieldMappingLocal(effectiveBoardId);
      if (!fieldMapping || (!fieldMapping.timelineColumnId && !fieldMapping.assigneesColumnId)) {
        monday.execute('notice', {
          message: 'Board field mapping is not configured. Please go to Settings > Boards and map the Timeline and People columns.',
          type: 'error',
        } as never);
        return;
      }
      if (!fieldMapping.timelineColumnId) {
        monday.execute('notice', {
          message: 'Timeline column is not mapped for this board. Please configure it in Settings > Boards.',
          type: 'error',
        } as never);
        return;
      }
      if (!fieldMapping.assigneesColumnId) {
        monday.execute('notice', {
          message: 'People column is not mapped for this board. Please configure it in Settings > Boards.',
          type: 'error',
        } as never);
        return;
      }

      // For subitems, write to the subitems board; for main items, write to the parent board
      const writeBoardId = isSubitem && subitemsBoardId ? subitemsBoardId : effectiveBoardId;

      // Optimistically add to store
      const idPrefix = isSubitem ? 'monday-sub' : 'monday';
      // Optimistic item uses 0h — real hours are loaded on refetch.
      // Avoids the "8h/d" flash before actual data arrives.
      const newItem: ScheduleItem = {
        id: `${idPrefix}-${effectiveBoardId}-${mondayItemId}-${assigneeId}`,
        mondayItemId,
        name: searchItem?.name ?? `Item #${mondayItemId}`,
        assigneeId,
        startDate: dateStr,
        endDate: dateStr,
        hours: 0,
        hoursPerDay: 0,
        hoursTotal: 0,
        workHourFormat: 'total',
        hasHoursConflict: false,
        color: searchItem?.groupColor ?? '#579bfc',
        boardId: effectiveBoardId,
        isSubitem: isSubitem || undefined,
        subitemsBoardId: subitemsBoardId || undefined,
        parentItemId: searchItem?.parentItemId,
      };
      useScheduleStore.getState().addItem(newItem);

      // Write to monday.com
      try {
        if (fieldMapping.timelineColumnId) {
          await updateItemTimeline(writeBoardId, mondayItemId, fieldMapping.timelineColumnId, dateStr, dateStr);
        }
        if (fieldMapping.assigneesColumnId && assigneeId > 0) {
          await updateItemAssignee(writeBoardId, mondayItemId, fieldMapping.assigneesColumnId, [assigneeId], assigneeKind);
        }
        // Re-fetch to get correct data (silent to avoid scheduler unmount)
        refetchSchedule({ silent: true });
      } catch (err) {
        console.error('Failed to plan item:', err);
        useScheduleStore.getState().removeItem(newItem.id);
      }
    },
    [boardId, multiBoardIds, refetchSchedule],
  );

  // ── Create Item handler ────────────────────────────────────────────────────

  const handleCreateItem = useCallback(
    async (data: {
      boardId: number;
      name: string;
      groupId: string;
      assignees: Array<{ id: number; kind: 'person' | 'team' }>;
      startDate: string;
      endDate: string;
      hours: number;
      hoursPerDay?: number;
      hoursTotal?: number;
      status?: string;
      parentItemId?: number;
    }) => {
      // Use the board ID from the dialog (in multi-board mode, user picks which board)
      const targetBoardId = data.boardId;

      try {
        const isSubitem = !!data.parentItemId;
        // Subitems use the parent board's subitem field mapping; main items use the board's own mapping
        const fieldMapping = isSubitem
          ? getSubitemFieldMappingLocal(targetBoardId)
          : getFieldMappingLocal(targetBoardId);

        console.log('[CreateItem] isSubitem:', isSubitem, 'boardId:', targetBoardId, 'fieldMapping:', fieldMapping);

        // Build column_values JSON to set all fields in a single create call
        const columnValues: Record<string, unknown> = {};
        if (fieldMapping?.timelineColumnId) {
          columnValues[fieldMapping.timelineColumnId] = { from: data.startDate, to: data.endDate };
        }
        if (fieldMapping?.assigneesColumnId && data.assignees.length > 0) {
          columnValues[fieldMapping.assigneesColumnId] = {
            personsAndTeams: data.assignees.map((a) => ({
              id: a.id,
              kind: a.kind === 'team' ? 'team' : 'person',
            })),
          };
        }
        if (fieldMapping?.scheduledHoursPerDayColumnId && data.hoursPerDay != null) {
          columnValues[fieldMapping.scheduledHoursPerDayColumnId] = String(data.hoursPerDay);
        }
        if (fieldMapping?.scheduledHoursColumnId && data.hoursTotal != null) {
          columnValues[fieldMapping.scheduledHoursColumnId] = String(data.hoursTotal);
        }
        if (fieldMapping?.statusColumnId && data.status) {
          columnValues[fieldMapping.statusColumnId] = { label: data.status };
        }

        const hasColumnValues = Object.keys(columnValues).length > 0;
        // monday.com JSON scalar expects a JSON-encoded string
        const columnValuesJson = hasColumnValues ? JSON.stringify(columnValues) : null;

        console.log('[CreateItem] columnValues:', columnValues, 'columnValuesJson:', columnValuesJson);

        let createdItem: { id: string; name: string } | undefined;

        if (isSubitem) {
          // Create subitem with column values in one call
          const mutation = columnValuesJson
            ? `mutation CreateSubitem($parentItemId: ID!, $itemName: String!, $columnValues: JSON!) {
                create_subitem(parent_item_id: $parentItemId, item_name: $itemName, column_values: $columnValues) { id name }
              }`
            : `mutation CreateSubitem($parentItemId: ID!, $itemName: String!) {
                create_subitem(parent_item_id: $parentItemId, item_name: $itemName) { id name }
              }`;
          const variables: Record<string, unknown> = {
            parentItemId: String(data.parentItemId),
            itemName: data.name,
          };
          if (columnValuesJson) variables.columnValues = columnValuesJson;
          const response = await monday.api(mutation, { variables });
          createdItem = (response.data as { create_subitem?: { id: string; name: string } })?.create_subitem;
        } else {
          // Create regular item with column values in one call
          const mutation = columnValuesJson
            ? `mutation CreateItem($boardId: ID!, $groupId: String!, $itemName: String!, $columnValues: JSON!) {
                create_item(board_id: $boardId, group_id: $groupId, item_name: $itemName, column_values: $columnValues) { id name }
              }`
            : `mutation CreateItem($boardId: ID!, $groupId: String!, $itemName: String!) {
                create_item(board_id: $boardId, group_id: $groupId, item_name: $itemName) { id name }
              }`;
          const variables: Record<string, unknown> = {
            boardId: String(targetBoardId),
            groupId: data.groupId,
            itemName: data.name,
          };
          if (columnValuesJson) variables.columnValues = columnValuesJson;
          const response = await monday.api(mutation, { variables });
          createdItem = (response.data as { create_item?: { id: string; name: string } })?.create_item;
        }
        if (!createdItem) return;

        const newItemId = Number(createdItem.id);

        // Optimistically add items to the store for each assignee so they appear
        // immediately without a full scheduler reload.
        const optimisticIds: string[] = [];
        for (const assignee of data.assignees) {
          const assigneeId = assignee.kind === 'person' ? assignee.id : 0;
          const assignedTeamId = assignee.kind === 'team' ? assignee.id : undefined;
          const optimisticId = `monday-${targetBoardId}-${newItemId}-${assignee.kind === 'team' ? `team-${assignee.id}` : assignee.id}`;
          optimisticIds.push(optimisticId);
          const newItem: ScheduleItem = {
            id: optimisticId,
            mondayItemId: newItemId,
            name: data.name,
            assigneeId,
            assignedTeamId,
            startDate: data.startDate,
            endDate: data.endDate,
            hours: data.hoursPerDay ?? data.hours,
            hoursPerDay: data.hoursPerDay ?? data.hours,
            hoursTotal: data.hoursTotal ?? 0,
            workHourFormat: data.hoursPerDay != null ? 'per_day' : 'total',
            hasHoursConflict: false,
            color: '#579bfc',
            status: data.status,
            boardId: targetBoardId,
            isSubitem: isSubitem || undefined,
            parentItemId: data.parentItemId,
          };
          useScheduleStore.getState().addItem(newItem);
        }

        // If no assignees were provided, still add one unassigned item
        if (data.assignees.length === 0) {
          const optimisticId = `monday-${targetBoardId}-${newItemId}-0`;
          optimisticIds.push(optimisticId);
          const newItem: ScheduleItem = {
            id: optimisticId,
            mondayItemId: newItemId,
            name: data.name,
            assigneeId: 0,
            startDate: data.startDate,
            endDate: data.endDate,
            hours: data.hoursPerDay ?? data.hours,
            hoursPerDay: data.hoursPerDay ?? data.hours,
            hoursTotal: data.hoursTotal ?? 0,
            workHourFormat: data.hoursPerDay != null ? 'per_day' : 'total',
            hasHoursConflict: false,
            color: '#579bfc',
            status: data.status,
            boardId: targetBoardId,
            isSubitem: isSubitem || undefined,
            parentItemId: data.parentItemId,
          };
          useScheduleStore.getState().addItem(newItem);
        }

        // Background refetch to sync real data (group color, correct IDs, etc.)
        // without visual reload — silent: true keeps the scheduler mounted.
        refetchSchedule({ silent: true });
      } catch (err) {
        console.error('Failed to create item:', err);
      }
    },
    [boardId, multiBoardIds, refetchSchedule],
  );

  // ── Context menu handlers (3-dot menu on events) ─────────────────────────

  const handleLogWork = useCallback(
    (taskId: string) => {
      setTooltipState(null);
      setLogWorkTaskId(taskId);
    },
    [],
  );

  const handleDuplicateItem = useCallback(
    async (taskId: string) => {
      const item = items.find((i) => i.id === taskId);
      if (!item) return;

      // For subitems, use the subitems board ID for duplication
      const effectiveBoardId = item.isSubitem && item.subitemsBoardId
        ? item.subitemsBoardId
        : (item.boardId ?? boardId);

      try {
        const mutation = `
          mutation DuplicateItem($boardId: ID!, $itemId: ID!) {
            duplicate_item(board_id: $boardId, item_id: $itemId) {
              id
              name
            }
          }
        `;
        await monday.api(mutation, {
          variables: {
            boardId: String(effectiveBoardId),
            itemId: String(item.mondayItemId),
          },
        });
        // Re-fetch schedule to pick up the duplicated item
        refetchSchedule({ silent: true });
      } catch (err) {
        console.error('Failed to duplicate item:', err);
      }
    },
    [boardId, multiBoardIds, items, refetchSchedule],
  );

  const handleUnplanItem = useCallback(
    async (taskId: string) => {
      const item = items.find((i) => i.id === taskId);
      if (!item) return;

      const effectiveBoardId = item.boardId ?? boardId;
      const fieldMapping = item.isSubitem
        ? getSubitemFieldMappingLocal(effectiveBoardId)
        : getFieldMappingLocal(effectiveBoardId);
      if (!fieldMapping) return;

      const writeBoardId = item.isSubitem && item.subitemsBoardId ? item.subitemsBoardId : effectiveBoardId;

      // Check if this is a multi-assignee item (has sibling schedule items)
      const siblings = items.filter(
        (i) => i.mondayItemId === item.mondayItemId && i.id !== taskId,
      );

      try {
        if (siblings.length > 0) {
          // Multi-assignee: remove only THIS user from the people column,
          // keep the timeline and other assignees intact.
          if (fieldMapping.assigneesColumnId) {
            // Build new assignee list WITHOUT the removed user
            const remainingEntries = siblings
              .map((sib) => {
                if (sib.assigneeId > 0) {
                  return { id: sib.assigneeId, kind: 'person' as const };
                }
                if (sib.assignedTeamId && sib.assignedTeamId > 0) {
                  return { id: sib.assignedTeamId, kind: 'team' as const };
                }
                return null;
              })
              .filter((e): e is { id: number; kind: 'person' | 'team' } => e !== null);

            // Deduplicate
            const uniqueEntries = new Map<string, { id: number; kind: 'person' | 'team' }>();
            for (const entry of remainingEntries) {
              uniqueEntries.set(`${entry.kind}-${entry.id}`, entry);
            }

            await updateItemAssignee(
              writeBoardId,
              item.mondayItemId,
              fieldMapping.assigneesColumnId,
              Array.from(uniqueEntries.values()),
            );
          }

          // Remove only this item from local store
          useScheduleStore.getState().removeItem(taskId);

          // Redistribute hours among remaining siblings
          const store = useScheduleStore.getState();
          const perPersonDist = getPerPersonDistributionLocal(effectiveBoardId) ??
            useWorkloadStore.getState().perPersonDistribution;
          redistributeHours(store.updateItem, store.items, item.mondayItemId, perPersonDist);
        } else {
          // Single assignee: clear the timeline column to fully unplan
          if (fieldMapping.timelineColumnId) {
            await updateItemTimeline(writeBoardId, item.mondayItemId, fieldMapping.timelineColumnId, '', '');
          }
          // Remove from local store
          useScheduleStore.getState().removeItem(taskId);
        }
      } catch (err) {
        console.error('Failed to unplan item:', err);
      }
    },
    [boardId, items],
  );

  /** Unplan an entire monday.com item — removes ALL assignee siblings from the schedule. */
  const handleUnplanEntireItem = useCallback(
    async (taskId: string) => {
      const item = items.find((i) => i.id === taskId);
      if (!item) return;

      const effectiveBoardId = item.boardId ?? boardId;
      const fieldMapping = item.isSubitem
        ? getSubitemFieldMappingLocal(effectiveBoardId)
        : getFieldMappingLocal(effectiveBoardId);
      if (!fieldMapping) return;

      const writeBoardId = item.isSubitem && item.subitemsBoardId ? item.subitemsBoardId : effectiveBoardId;

      // Remove ALL siblings (including the clicked one) from local store immediately
      const allSiblings = items.filter((i) => i.mondayItemId === item.mondayItemId);
      const store = useScheduleStore.getState();
      for (const sib of allSiblings) {
        store.removeItem(sib.id);
      }

      try {
        // Clear the timeline column on monday.com to fully unplan
        if (fieldMapping.timelineColumnId) {
          await updateItemTimeline(writeBoardId, item.mondayItemId, fieldMapping.timelineColumnId, '', '');
        }
      } catch (err) {
        console.error('Failed to unplan item:', err);
      }
    },
    [boardId, items],
  );

  const handleSaveTask = useCallback(
    async (
      taskId: string,
      updates: {
        name?: string;
        assignees?: Array<{ id: number; kind: 'person' | 'team' }>;
        startDate?: string;
        endDate?: string;
        hours?: number;
        hoursPerDay?: number;
        hoursTotal?: number;
        status?: string;
      },
    ) => {
      const item = items.find((i) => i.id === taskId);
      if (!item) return;

      // Update local store (assignees handled separately via refetch)
      const storeUpdates: Partial<ScheduleItem> = {};
      if (updates.name) storeUpdates.name = updates.name;
      if (updates.startDate) storeUpdates.startDate = updates.startDate;
      if (updates.endDate) storeUpdates.endDate = updates.endDate;
      // When both hour fields are provided (both columns mapped), update both
      if (updates.hoursPerDay !== undefined) {
        storeUpdates.hoursPerDay = updates.hoursPerDay;
      }
      if (updates.hoursTotal !== undefined) {
        storeUpdates.hoursTotal = updates.hoursTotal;
      }
      // Fallback: single hours field (only one column mapped)
      if (updates.hours !== undefined && updates.hoursPerDay === undefined && updates.hoursTotal === undefined) {
        if (item.workHourFormat === 'per_day') {
          storeUpdates.hoursPerDay = updates.hours;
        } else {
          storeUpdates.hoursTotal = updates.hours;
        }
      }
      const hoursChanged = storeUpdates.hoursPerDay !== undefined || storeUpdates.hoursTotal !== undefined;
      if (updates.status !== undefined) storeUpdates.status = updates.status;
      if (Object.keys(storeUpdates).length > 0) {
        const store = useScheduleStore.getState();
        // Apply raw field updates to ALL siblings so redistributeHours picks up the new total
        const siblings = store.items.filter((i) => i.mondayItemId === item.mondayItemId);
        for (const sib of siblings) {
          store.updateItem(sib.id, storeUpdates);
        }
        // Redistribute split hours across all assignees
        if (hoursChanged) {
          const freshStore = useScheduleStore.getState();
          const effBoardId = item.boardId ?? boardId;
          const perPersonDist = getPerPersonDistributionLocal(effBoardId) ??
            useWorkloadStore.getState().perPersonDistribution;
          redistributeHours(freshStore.updateItem, freshStore.items, item.mondayItemId, perPersonDist);
        }
      }

      const effectiveBoardId = item.boardId ?? boardId;
      const fieldMapping = item.isSubitem
        ? getSubitemFieldMappingLocal(effectiveBoardId)
        : getFieldMappingLocal(effectiveBoardId);
      if (!fieldMapping) return;

      const writeBoardId = item.isSubitem && item.subitemsBoardId ? item.subitemsBoardId : effectiveBoardId;

      let needsRefetch = false;

      try {
        // Rename item on monday.com
        if (updates.name) {
          await monday.api(
            `mutation ($boardId: ID!, $itemId: ID!, $value: String!) {
              change_simple_column_value(board_id: $boardId, item_id: $itemId, column_id: "name", value: $value) { id }
            }`,
            { variables: { boardId: String(writeBoardId), itemId: String(item.mondayItemId), value: updates.name } },
          );
        }

        // Write timeline changes to monday.com
        if (
          fieldMapping.timelineColumnId &&
          (updates.startDate || updates.endDate)
        ) {
          const newStart = updates.startDate ?? item.startDate;
          const newEnd = updates.endDate ?? item.endDate;
          await updateItemTimeline(
            writeBoardId,
            item.mondayItemId,
            fieldMapping.timelineColumnId,
            newStart,
            newEnd,
          );
        }

        // Write assignee changes — full replacement from the multi-select picker
        if (
          fieldMapping.assigneesColumnId &&
          updates.assignees !== undefined
        ) {
          await updateItemAssignee(
            writeBoardId,
            item.mondayItemId,
            fieldMapping.assigneesColumnId,
            updates.assignees,
          );
          needsRefetch = true;
        }

        // Write hours changes to monday.com
        if (updates.hoursPerDay !== undefined && fieldMapping.scheduledHoursPerDayColumnId) {
          await updateMondayItem(
            writeBoardId,
            item.mondayItemId,
            fieldMapping.scheduledHoursPerDayColumnId,
            JSON.stringify(String(updates.hoursPerDay)),
          );
        }
        if (updates.hoursTotal !== undefined && fieldMapping.scheduledHoursColumnId) {
          await updateMondayItem(
            writeBoardId,
            item.mondayItemId,
            fieldMapping.scheduledHoursColumnId,
            JSON.stringify(String(updates.hoursTotal)),
          );
        }
        // Fallback: single hours field (only one column mapped)
        if (updates.hours !== undefined && updates.hoursPerDay === undefined && updates.hoursTotal === undefined) {
          const targetColumnId = item.workHourFormat === 'per_day'
            ? fieldMapping.scheduledHoursPerDayColumnId
            : fieldMapping.scheduledHoursColumnId;
          if (targetColumnId) {
            await updateMondayItem(
              writeBoardId,
              item.mondayItemId,
              targetColumnId,
              JSON.stringify(String(updates.hours)),
            );
          }
        }

        // Write status changes to monday.com
        if (updates.status !== undefined && fieldMapping.statusColumnId) {
          await monday.api(
            `mutation ($boardId: ID!, $itemId: ID!, $columnId: String!, $value: JSON!) {
              change_column_value(board_id: $boardId, item_id: $itemId, column_id: $columnId, value: $value) { id }
            }`,
            {
              variables: {
                boardId: String(writeBoardId),
                itemId: String(item.mondayItemId),
                columnId: fieldMapping.statusColumnId,
                value: JSON.stringify({ label: updates.status }),
              },
            },
          );
          needsRefetch = true;
        }

        // Refetch once after all API writes are complete to avoid overwriting optimistic updates
        if (needsRefetch) {
          refetchSchedule({ silent: true });
        }
      } catch (err) {
        console.error('Failed to update item:', err);
      }
    },
    [boardId, items, refetchSchedule],
  );

  const handleOpenInMonday = useCallback(
    (mondayItemId: number) => {
      monday.execute('openItemCard', { itemId: mondayItemId } as never);
    },
    [],
  );

  const selectedTask = items.find((item) => item.id === selectedTaskId) ?? null;

  // Show the field mapping wizard if required fields are not configured (only after config is loaded)
  if (configLoaded && showWizard) {
    return (
      <FieldMappingWizard
        boardId={boardId}
        onComplete={() => setShowWizard(false)}
        onSkip={() => setShowWizard(false)}
      />
    );
  }

  return (
    <div className="rp-scheduler-board">
      {/* Top control bar */}
      <TopBar
        onOpenSettings={() => setIsSettingsOpen(true)}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        onExportExcel={handleExportExcel}
        isExporting={isExporting}
      />

      {/* Filter bar */}
      <FilterBar
        assigneeOptions={assigneeOptions}
        teamOptions={teamOptions}
        groupByMode={groupByMode}
        onGroupByModeChange={setGroupByMode}
        boardOptions={multiBoardOptions}
        hasStatusColumn={!!getFieldMappingLocal(boardId)?.statusColumnId}
        onToggleSearchPanel={() => {
          // In no-boards mode the Plan Item button has nothing to search —
          // prompt the user to add a board instead of opening an empty panel.
          if (noBoardsMode) {
            setShowNoBoardsPrompt(true);
            return;
          }
          setIsSearchPanelOpen((prev) => !prev);
          setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
        }}
        isSearchPanelOpen={isSearchPanelOpen}
      />

      {/* Content row: optional search panel + scheduler */}
      <div className="rp-scheduler-board__content-row">
        {/* Left panel: Item search (hidden in leaves-only mode — no items to search) */}
        {isSearchPanelOpen && !noBoardsMode && (
          <ItemSearchPanel
            boardId={boardId}
            multiBoardIds={multiBoardIds}
            multiBoardOptions={multiBoardOptions}
            onClose={() => {
              setIsSearchPanelOpen(false);
              setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
            }}
          />
        )}

        <div className="rp-scheduler-board__main">
          {/* Loading / Error banner */}
          {isLoading && (
            <div style={{ padding: '8px 16px', color: '#676879', fontSize: '13px' }}>
              Loading schedule...
            </div>
          )}
          {error && (
            <div style={{ padding: '8px 16px', color: '#d83a52', fontSize: '13px', background: '#fef2f2' }}>
              Error: {error}
            </div>
          )}

          {/* Selection prompt when no resources are selected */}
          {!isLoading && groupByMode === 'assignees' && filterAssignees.length === 0 && filterTeams.length === 0 && (
            <div className="rp-team-select-prompt">
              <div className="rp-team-select-prompt__content">
                <h3>Select Resources</h3>
                <p>Choose one or more users or teams from the dropdown above to display in the scheduler.</p>
              </div>
            </div>
          )}
          {!isLoading && (groupByMode === 'teams' || groupByMode === 'teamWithUsers') && filterTeams.length === 0 && (
            <div className="rp-team-select-prompt">
              <div className="rp-team-select-prompt__content">
                <h3>Select Teams</h3>
                <p>Choose one or more teams from the dropdown above to display in the scheduler.</p>
              </div>
            </div>
          )}

          {/* Syncfusion Scheduler */}
          {!isLoading && ((groupByMode === 'assignees' && (filterAssignees.length > 0 || filterTeams.length > 0)) || ((groupByMode === 'teams' || groupByMode === 'teamWithUsers') && filterTeams.length > 0)) && (
            <SyncfusionScheduler
              ref={schedulerRef}
              boardId={boardId}
              items={filteredItems}
              userMap={filteredUserMap}
              viewMode={viewMode}
              dateRange={dateRange}
              holidays={showHolidays ? holidays : undefined}
              userHolidays={showHolidays ? userHolidays : undefined}
              groupByMode={groupByMode}
              selectedTeamIds={filterTeams}
              teamMemberMap={teamMemberMap}
              leaves={showLeaves ? leaves : undefined}
              currentUserId={context.userId ? Number(context.userId) : undefined}
              onLeaveResized={async (leaveId, newStart, newEnd) => {
                try {
                  await updateLeaveRequest(leaveId, { dateFrom: newStart, dateTo: newEnd });
                } catch {
                  // Revert on failure by refetching
                }
                await fetchLeaves();
              }}
              onTaskClick={(taskId, position) => {
                if (position) {
                  setTooltipState({ taskId, position });
                } else {
                  setSelectedTaskId(taskId);
                }
              }}
              onLeaveClick={(leaveId, position) => {
                if (position) {
                  setLeaveTooltipState({ leaveId, position });
                }
              }}
              onCellClick={(date, assigneeId, endDate, assigneeKind, x, y) => {
                // Toggle: any cell click while the menu is already open closes
                // it, regardless of whether it's a fast double-click or a slow
                // second click. Clicking a different cell still acts as a
                // close (next click will re-open where wanted).
                setCellMenu((prev) =>
                  prev ? null : { x, y, date, endDate, assigneeId, assigneeKind },
                );
              }}
              onExternalDrop={handleExternalDrop}
              worklogEvents={indicatorType === 'worklog_workload' ? worklogEventsData : undefined}
              onWorklogClick={handleWorklogClick}
            />
          )}
        </div>
      </div>

      {/* Worklog detail popup */}
      {worklogPopup && (() => {
        const wl = storeRawWorklogs.find((w) => w.id === worklogPopup.worklogId);
        if (!wl) return null;
        const isOwner = context.userId ? String(context.userId) === wl.mondayUserId : false;
        const canEdit = isOwner || isAdmin;
        return (
          <WorklogDetailPopup
            worklog={wl}
            position={worklogPopup.position}
            localItems={items}
            canDelete={canEdit}
            onDelete={() => handleWorklogDelete(wl.id)}
            onClose={() => setWorklogPopup(null)}
          />
        );
      })()}

      {/* Item Worklogs Dialog */}
      {itemWorklogsDialog && (
        <ItemWorklogsDialog
          mondayItemId={itemWorklogsDialog.mondayItemId}
          itemName={itemWorklogsDialog.itemName}
          onClose={() => setItemWorklogsDialog(null)}
        />
      )}

      {/* Multi-select toolbar */}
      <MultiSelectToolbar
        selectedItemIds={selectedItemIds}
        assigneeOptions={assigneeOptions}
        onUnplan={handleUnplan}
        onReassign={handleReassign}
        onMoveToDate={handleMoveToDate}
        onShiftDuration={handleShiftDuration}
        onCancelSelection={() => setSelectedItemIds([])}
      />

      {/* Task Info Tooltip (shown on single click) */}
      {tooltipState && (() => {
        const tooltipTask = items.find((i) => i.id === tooltipState.taskId);
        if (!tooltipTask) return null;
        // Collect all assignee names for this monday item
        const siblings = items.filter((i) => i.mondayItemId === tooltipTask.mondayItemId);
        const assigneeNames: string[] = [];
        const seen = new Set<string>();
        for (const sib of siblings) {
          // Person items (assigneeId > 0) take priority — in teamWithUsers mode
          // they may also carry assignedTeamId as a UI-only field.
          if (sib.assigneeId > 0) {
            const key = `person-${sib.assigneeId}`;
            if (!seen.has(key)) {
              seen.add(key);
              assigneeNames.push(getAssigneeName(sib));
            }
          } else if (sib.assignedTeamId && sib.assignedTeamId > 0) {
            const key = `team-${sib.assignedTeamId}`;
            if (!seen.has(key)) {
              seen.add(key);
              assigneeNames.push(getAssigneeName(sib));
            }
          }
        }
        const parentName = tooltipTask.parentItemName;
        return (
          <TaskInfoTooltip
            task={tooltipTask}
            assigneeName={assigneeNames.join(', ')}
            parentItemName={parentName}
            position={tooltipState.position}
            isMultiBoard={multiBoardIds != null && multiBoardIds.length > 1}
            onEdit={(taskId) => {
              setTooltipState(null);
              setSelectedTaskId(taskId);
            }}
            onLogWork={handleLogWork}
            onViewWorklogs={(mondayItemId, pos) => {
              const itemName = items.find((i) => i.mondayItemId === mondayItemId)?.name ?? '';
              setItemWorklogsDialog({ mondayItemId, itemName, position: pos });
            }}
            onDuplicate={(taskId) => {
              setTooltipState(null);
              handleDuplicateItem(taskId);
            }}
            onUnplan={(taskId) => {
              setTooltipState(null);
              handleUnplanItem(taskId);
            }}
            onOpenInMonday={handleOpenInMonday}
            onClose={() => setTooltipState(null)}
          />
        );
      })()}

      {/* Task Edit Dialog (centered modal — same style as Create Item) */}
      {selectedTask && (
        <TaskEditDialog
          task={selectedTask}
          allItems={items}
          assigneeName={getAssigneeName(selectedTask)}
          assigneeOptions={assigneeOptions}
          teamOptions={teamOptions}
          onClose={() => setSelectedTaskId(null)}
          onSave={handleSaveTask}
          onRemove={(taskId) => {
            handleUnplanEntireItem(taskId);
            setSelectedTaskId(null);
          }}
          onOpenInMonday={handleOpenInMonday}
        />
      )}

      {/* Log Work Dialog */}
      {logWorkTaskId && (() => {
        const logWorkTask = items.find((i) => i.id === logWorkTaskId);
        if (!logWorkTask) return null;
        const logWorkBoardId = logWorkTask.boardId ?? boardId;
        const taskOwnerId = logWorkTask.assigneeId;
        const isOwnTask = taskOwnerId === Number(context.userId);
        // Impersonate when the caller can log time for the task owner:
        // admin / app-admin, any-target timesheet approver, or per-user
        // timesheet approver. Leave approvers don't qualify.
        const canImpersonate = !isOwnTask && !!taskOwnerId && (
          isAdmin
          || isAppAdmin
          || canLogTimeForAnyUser
          || timesheetApproveeIds.has(taskOwnerId)
        );
        return (
          <LogWorkDialog
            task={logWorkTask}
            boardId={logWorkBoardId}
            assigneeName={getAssigneeName(logWorkTask)}
            currentUserId={String(context.userId ?? 0)}
            forUserId={canImpersonate ? String(taskOwnerId) : undefined}
            forUserName={canImpersonate ? (userMap.get(taskOwnerId)?.name ?? `User ${taskOwnerId}`) : undefined}
            onClose={() => setLogWorkTaskId(null)}
            onWorkLogged={() => {
              // Worklogs changed — invalidate cache and recompute
              const currentIndicator = useWorkloadStore.getState().indicatorType;
              if (currentIndicator === 'remaining_estimate' || currentIndicator === 'worklog_workload' || currentIndicator === 'availability') {
                invalidateWorklogCache();
                const visibleUserIds = users.filter((u) => u.id !== 0).map((u) => u.id);
                const approvedLeaves = workloadLeaves.filter((l) => l.status === 'Approved');
                computeWorkload(visibleUserIds, filteredItems, approvedLeaves, allUserHolidays);
              }
            }}
          />
        );
      })()}

      {/* Side panel: Board settings (unavailable in leaves-only mode — no board to configure) */}
      {isSettingsOpen && !noBoardsMode && (
        <BoardSettings
          boardId={boardId}
          onClose={() => setIsSettingsOpen(false)}
          onSaved={refetchSchedule}
          multiBoardOptions={multiBoardOptions}
        />
      )}

      {/* Leave Info Tooltip (shown on leave event click) */}
      {leaveTooltipState && (() => {
        const leave = leaves.find((l) => l.id === leaveTooltipState.leaveId);
        if (!leave) return null;
        const assigneeName = userMap.get(leave.assigneeId)?.name ?? 'Unknown';
        return (
          <LeaveInfoTooltip
            leave={leave}
            assigneeName={assigneeName}
            currentUserId={Number(context.userId) || 0}
            isAppAdmin={isAdmin || isAppAdmin}
            position={leaveTooltipState.position}
            onEdit={(leaveId) => {
              setLeaveTooltipState(null);
              const rawReq = rawLeaveRequests.find((r) => r.id === leaveId);
              if (rawReq) {
                setEditingLeaveRequest(rawReq);
                setLeaveDialogOpen(true);
              }
            }}
            onDelete={async (id) => {
              setLeaveTooltipState(null);
              try {
                await recallLeaveRequest(id);
              } catch {
                // Silently fail if recall is not allowed for this status
              }
              await fetchLeaves();
            }}
            onApprove={async (id) => {
              setLeaveTooltipState(null);
              try {
                await approveLeaveRequest(id);
              } catch {
                // Silently fail if approve fails
              }
              await fetchLeaves();
            }}
            onReject={async (id) => {
              setLeaveTooltipState(null);
              try {
                await rejectLeaveRequest(id);
              } catch {
                // Silently fail if reject fails
              }
              await fetchLeaves();
            }}
            onRevoke={async (id) => {
              setLeaveTooltipState(null);
              try {
                await revokeLeaveRequest(id);
              } catch {
                // Silently fail if revoke is rejected by the backend
              }
              await fetchLeaves();
            }}
            onClose={() => setLeaveTooltipState(null)}
          />
        );
      })()}

      {/* Create Item Dialog — hidden in leaves-only mode; cell clicks route to LeaveRequestDialog instead */}
      {createDialogState.open && !noBoardsMode && (
        <CreateItemDialog
          boardId={boardId}
          initialDate={createDialogState.date}
          initialEndDate={createDialogState.endDate}
          initialAssigneeId={createDialogState.assigneeId}
          initialAssigneeKind={createDialogState.assigneeKind}
          assigneeOptions={assigneeOptions}
          teamOptions={teamOptions}
          groupOptions={boardGroups}
          multiBoardOptions={multiBoardOptions}
          itemLevel={getItemLevelLocal(boardId)}
          onClose={() => {
            setCreateDialogState({ open: false });
            schedulerRef.current?.clearSelection();
          }}
          onCreateItem={handleCreateItem}
          onOpenLeaveDialog={
            createDialogState.assigneeKind === 'team'
              ? undefined
              : () => {
                  const dateStr = createDialogState.date
                    ? `${createDialogState.date.getFullYear()}-${String(createDialogState.date.getMonth() + 1).padStart(2, '0')}-${String(createDialogState.date.getDate()).padStart(2, '0')}`
                    : undefined;
                  const clickedAssigneeId = createDialogState.assigneeId;
                  setCreateDialogState({ open: false });
                  setLeaveDialogDate(dateStr);
                  // When clicking on another user's row, pre-select them
                  if (clickedAssigneeId && clickedAssigneeId !== Number(context.userId)) {
                    setLeaveDialogForUserId(clickedAssigneeId);
                  } else {
                    setLeaveDialogForUserId(undefined);
                  }
                  setLeaveDialogOpen(true);
                }
          }
        />
      )}

      {/* Cell context menu — opens on single-click of an empty scheduler cell.
          Offers "Request leave" (person rows only) and "Create item" (disabled in
          noBoardsMode). Replaces the prior double-click → CreateItemDialog flow. */}
      {cellMenu && (
        <CellContextMenu
          x={cellMenu.x}
          y={cellMenu.y}
          assigneeKind={cellMenu.assigneeKind}
          assigneeName={userMap.get(cellMenu.assigneeId)?.name}
          isSelfRow={cellMenu.assigneeId === Number(context.userId)}
          // Each action is gated on the permission that actually governs it,
          // so a leave approver doesn't get Log time and a timesheet approver
          // doesn't get Request leave (the symmetric set would mislabel the
          // option).
          //  - canRequestLeave: self, admin, or caller is the clicked user's
          //    default leave approver.
          //  - canLogTime: self, admin, caller has a timesheet_approve rule
          //    targeting the clicked user, or the 'all'-target equivalent.
          canRequestLeave={
            cellMenu.assigneeKind === 'person'
            && (
              cellMenu.assigneeId === Number(context.userId)
              || isAdmin
              || isAppAdmin
              || leaveApproveeIds.has(cellMenu.assigneeId)
            )
          }
          canLogTime={
            cellMenu.assigneeKind === 'person'
            && (
              cellMenu.assigneeId === Number(context.userId)
              || isAdmin
              || isAppAdmin
              || canLogTimeForAnyUser
              || timesheetApproveeIds.has(cellMenu.assigneeId)
            )
          }
          onClose={() => setCellMenu(null)}
          onCreateItem={() => {
            // In no-boards mode there's nothing to create against; prompt the
            // user to return to Board setup instead.
            if (noBoardsMode) {
              setShowNoBoardsPrompt(true);
              setCellMenu(null);
              return;
            }
            setCreateDialogState({
              open: true,
              date: cellMenu.date,
              endDate: cellMenu.endDate,
              assigneeId: cellMenu.assigneeId,
              assigneeKind: cellMenu.assigneeKind,
            });
            setCellMenu(null);
          }}
          onRequestLeave={() => {
            const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            setLeaveDialogDate(fmt(cellMenu.date));
            setLeaveDialogEndDate(cellMenu.endDate ? fmt(cellMenu.endDate) : undefined);
            setLeaveDialogForUserId(
              cellMenu.assigneeId && cellMenu.assigneeId !== Number(context.userId)
                ? cellMenu.assigneeId
                : undefined,
            );
            setLeaveDialogOpen(true);
            setCellMenu(null);
          }}
          onLogTime={() => {
            const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const isOther = cellMenu.assigneeId && cellMenu.assigneeId !== Number(context.userId);
            // Mirror the Log time visibility gate: admins, any-target
            // timesheet approvers, or per-user timesheet approvers can
            // impersonate. Leave approvees are intentionally excluded —
            // approving someone's leave doesn't grant rights to log their work.
            const canImpersonate = (
              isAdmin
              || isAppAdmin
              || canLogTimeForAnyUser
              || (cellMenu.assigneeId && timesheetApproveeIds.has(cellMenu.assigneeId))
            ) && isOther;
            setLogTimeDialog({
              initialDate: fmt(cellMenu.date),
              initialEndDate: cellMenu.endDate ? fmt(cellMenu.endDate) : undefined,
              forUserId: canImpersonate ? String(cellMenu.assigneeId) : undefined,
              forUserName: canImpersonate
                ? userMap.get(cellMenu.assigneeId)?.name ?? `User ${cellMenu.assigneeId}`
                : undefined,
            });
            setCellMenu(null);
          }}
        />
      )}

      {/* Prompt shown when the user tries to plan or create an item in
          no-boards mode (via the cell menu or the top-right Plan Item button). */}
      <Modal
        show={showNoBoardsPrompt}
        onClose={() => setShowNoBoardsPrompt(false)}
        title="Add a board to work with items"
      >
        <ModalHeader title="Add a board to work with items" />
        <ModalContent>
          <Text style={{ whiteSpace: 'normal', overflow: 'visible', textOverflow: 'unset', marginTop: 12 }}>
            You don&apos;t have any boards selected yet. Click <strong>Board setup</strong>
            {' '}at the top of the scheduler to add a board — then you can plan existing
            items or create new ones.
          </Text>
        </ModalContent>
        <ModalFooterButtons
          primaryButtonText="Got it"
          onPrimaryButtonClick={() => setShowNoBoardsPrompt(false)}
        />
      </Modal>

      {/* Log time dialog (time-tracking mode — user picks the item). Opened from
          the cell context menu's "Log time" action. */}
      {logTimeDialog && (
        <LogWorkDialog
          currentUserId={String(context.userId ?? 0)}
          forUserId={logTimeDialog.forUserId}
          forUserName={logTimeDialog.forUserName}
          initialDate={logTimeDialog.initialDate}
          initialEndDate={logTimeDialog.initialEndDate}
          onClose={() => setLogTimeDialog(null)}
          onWorkLogged={() => {
            const currentIndicator = useWorkloadStore.getState().indicatorType;
            if (
              currentIndicator === 'remaining_estimate' ||
              currentIndicator === 'worklog_workload' ||
              currentIndicator === 'availability'
            ) {
              invalidateWorklogCache();
              const visibleUserIds = users.filter((u) => u.id !== 0).map((u) => u.id);
              const approvedLeaves = workloadLeaves.filter((l) => l.status === 'Approved');
              computeWorkload(visibleUserIds, filteredItems, approvedLeaves, allUserHolidays);
            }
          }}
        />
      )}

      {/* Leave Request Dialog */}
      {leaveDialogOpen && context.userId && (
        <LeaveRequestDialog
          currentUserId={context.userId}
          initialDate={leaveDialogDate}
          initialEndDate={leaveDialogEndDate}
          editingRequest={editingLeaveRequest ?? undefined}
          forUserId={leaveDialogForUserId}
          forUserName={leaveDialogForUserId ? (userMap.get(leaveDialogForUserId)?.name ?? `User ${leaveDialogForUserId}`) : undefined}
          isAdmin={isAppAdmin}
          onSubmitted={() => {
            // Refetch leaves from backend so the new/updated one appears
            fetchLeaves();
            setLeaveDialogOpen(false);
            setEditingLeaveRequest(null);
            setLeaveDialogForUserId(undefined);
            setLeaveDialogEndDate(undefined);
          }}
          onClose={() => {
            setLeaveDialogOpen(false);
            setEditingLeaveRequest(null);
            setLeaveDialogForUserId(undefined);
            setLeaveDialogEndDate(undefined);
          }}
        />
      )}
    </div>
  );
};

export default SchedulerBoard;
